import React, { useState, useEffect } from 'react';
import { 
  Scale, 
  X, 
  Cookie, 
  EyeOff, 
  Accessibility, 
  RefreshCw, 
  CheckCircle, 
  ShieldCheck, 
  Bot, 
  AlertOctagon, 
  ArrowRight,
  Eye,
  HelpCircle,
  Layers
} from 'lucide-react';
import useLang from '../hooks/useLang';

const STORAGE_HIDDEN_KEY = 'adca_compliance_widget_hidden';

const translations = {
  es: {
    hubTitle: 'Ventanilla Única de Cumplimiento',
    hubSubtitle: 'ElectroPyme S.L. & ADCA Hub',
    status: '100% Conforme y Seguro',
    closeAria: 'Cerrar',
    tabCookies: 'Cookies',
    tabDenuncias: 'Canal Denuncias',
    tabAccess: 'Accesibilidad',
    tabDesist: 'Desistimiento',
    cookieInfo: 'Configure las cookies del sitio según sus preferencias de privacidad. Las cookies esenciales siempre permanecen activas.',
    cookieEssential: 'Cookies Esenciales (Técnicas)',
    cookieEssentialDesc: 'Permiten la navegación y persistencia de sesión.',
    cookieAnalytics: 'Cookies de Análisis',
    cookieAnalyticsDesc: 'Estadísticas de navegación anónimas.',
    cookieMarketing: 'Cookies de Publicidad',
    cookieMarketingDesc: 'Anuncios relevantes y remarketing.',
    btnAcceptConfig: 'Aceptar Configuración',
    btnAcceptAll: 'Aceptar Todo',
    denunciaTitle: 'Canal de Denuncias Seguro (Ley 2/2023)',
    denunciaDesc: 'Garantiza el anonimato absoluto mediante encriptación asimétrica RSA-4096 local y eliminación automática de metadatos de archivos adjuntos.',
    denunciaLabelType: 'Tipo de irregularidad:',
    denunciaLabelText: 'Descripción de los hechos:',
    denunciaType1: 'Acoso laboral o discriminación',
    denunciaType2: 'Fraude contable o fiscal',
    denunciaType3: 'Infracción de protección de datos (RGPD)',
    denunciaType4: 'Otras irregularidades legales',
    btnSendDenuncia: 'Enviar Alerta Anónima',
    accessTitle: 'Accesibilidad Adaptativa (RD 1112)',
    accessDesc: 'Ajuste la interfaz del sitio web según sus necesidades visuales o motoras de forma inmediata.',
    accessTextBig: 'Texto Grande',
    accessTextNormal: 'Texto Normal',
    accessContrast: 'Contraste Alto',
    accessDyslexia: 'Fuente Dislexia',
    desistTitle: 'Derecho de Desistimiento UE',
    desistDesc: 'Gestione la devolución de su compra en un solo clic. Calculamos el hash de integridad criptográfico de su resguardo.',
    desistLabelPedido: 'Código de Pedido:',
    desistLabelMotivo: 'Motivo de devolución (Opcional):',
    btnSendDesist: 'Tramitar Desistimiento Criptográfico',
    demoConsoleSuccess: '¡ENVIADO ANÓNIMAMENTE CON ÉXITO!',
    demoConsoleSuccessDesist: 'DEVOLUCIÓN REGISTRADA CORRECTAMENTE',
    footerInfo: 'ADCA Compliance Hub v1.0 · CERTIFICADO ENS',
    hideWidgetTooltip: 'Quitar ventanilla de la pantalla'
  },
  en: {
    hubTitle: 'One-Stop Compliance Desk',
    hubSubtitle: 'ElectroPyme S.L. & ADCA Hub',
    status: '100% Compliant & Secure',
    closeAria: 'Close',
    tabCookies: 'Cookies',
    tabDenuncias: 'Whistleblowing',
    tabAccess: 'Accessibility',
    tabDesist: 'Withdrawal',
    cookieInfo: 'Configure site cookies according to your privacy preferences. Essential cookies always remain active.',
    cookieEssential: 'Essential Cookies (Technical)',
    cookieEssentialDesc: 'Allow site navigation and session persistence.',
    cookieAnalytics: 'Analytical Cookies',
    cookieAnalyticsDesc: 'Anonymous navigation statistics.',
    cookieMarketing: 'Marketing Cookies',
    cookieMarketingDesc: 'Relevant ads and remarketing.',
    btnAcceptConfig: 'Accept Configuration',
    btnAcceptAll: 'Accept All',
    denunciaTitle: 'Secure Whistleblowing Channel (Law 2/2023)',
    denunciaDesc: 'Ensures absolute anonymity by local RSA-4096 asymmetric encryption and automatic metadata removal from attachments.',
    denunciaLabelType: 'Type of irregularity:',
    denunciaLabelText: 'Description of facts:',
    denunciaType1: 'Workplace harassment or discrimination',
    denunciaType2: 'Accounting or tax fraud',
    denunciaType3: 'Data protection violation (GDPR)',
    denunciaType4: 'Other legal irregularities',
    btnSendDenuncia: 'Send Anonymous Alert',
    accessTitle: 'Adaptive Accessibility (RD 1112)',
    accessDesc: 'Adjust the website interface according to your visual or motor needs immediately.',
    accessTextBig: 'Large Text',
    accessTextNormal: 'Normal Text',
    accessContrast: 'High Contrast',
    accessDyslexia: 'Dyslexia Font',
    desistTitle: 'EU Right of Withdrawal',
    desistDesc: 'Manage your purchase return in one single click. We calculate the cryptographic integrity hash of your receipt.',
    desistLabelPedido: 'Order Code:',
    desistLabelMotivo: 'Reason for return (Optional):',
    btnSendDesist: 'Process Cryptographic Withdrawal',
    demoConsoleSuccess: 'ANONYMOUSLY SENT SUCCESSFULLY!',
    demoConsoleSuccessDesist: 'RETURN REGISTERED SUCCESSFULLY',
    footerInfo: 'ADCA Compliance Hub v1.0 · ENS CERTIFIED',
    hideWidgetTooltip: 'Remove compliance desk from screen'
  }
};

export const ComplianceWidget = () => {
  const lang = useLang();
  const t = translations[lang] || translations.es;

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('cookies');

  // Form states
  const [cookieAnalyticsChecked, setCookieAnalyticsChecked] = useState(true);
  const [cookieMarketingChecked, setCookieMarketingChecked] = useState(false);

  const [denunciaType, setDenunciaType] = useState('acoso');
  const [denunciaDesc, setDenunciaDesc] = useState('');
  const [denunciaSubmitted, setDenunciaSubmitted] = useState(false);

  const [desistPedido, setDesistPedido] = useState('');
  const [desistMotivo, setDesistMotivo] = useState('');
  const [desistSubmitted, setDesistSubmitted] = useState(false);

  // Accessibility actual states
  const [textBigActive, setTextBigActive] = useState(false);
  const [contrastActive, setContrastActive] = useState(false);
  const [dyslexicActive, setDyslexicActive] = useState(false);

  useEffect(() => {
    const isHidden = localStorage.getItem(STORAGE_HIDDEN_KEY);
    if (isHidden === '1') {
      setVisible(false);
    }
  }, []);

  const handleHideWidget = (e) => {
    e.stopPropagation(); // Evitar abrir el widget al pulsar la cruz del botón
    localStorage.setItem(STORAGE_HIDDEN_KEY, '1');
    setVisible(false);
    setOpen(false);
  };

  const handleSaveCookies = () => {
    alert(lang === 'es' ? 'Preferencias de cookies guardadas.' : 'Cookie preferences saved.');
  };

  const handleAcceptAllCookies = () => {
    setCookieAnalyticsChecked(true);
    setCookieMarketingChecked(true);
    alert(lang === 'es' ? 'Se han aceptado todas las cookies.' : 'All cookies accepted.');
  };

  const handleDenunciaSubmit = (e) => {
    e.preventDefault();
    if (!denunciaDesc.trim()) {
      alert(lang === 'es' ? 'Escribe los hechos de la irregularidad.' : 'Please write down the facts.');
      return;
    }
    setDenunciaSubmitted(true);
  };

  const handleDesistSubmit = (e) => {
    e.preventDefault();
    if (!desistPedido.trim()) {
      alert(lang === 'es' ? 'Por favor introduce tu código de pedido.' : 'Please enter your order code.');
      return;
    }
    setDesistSubmitted(true);
  };

  // ACCESIBILIDAD FLUIDA REAL: Aplica clases en caliente sobre document.documentElement
  const handleToggleTextBig = () => {
    setTextBigActive(!textBigActive);
    if (!textBigActive) {
      document.documentElement.style.fontSize = '112.5%';
    } else {
      document.documentElement.style.fontSize = '';
    }
  };

  const handleToggleContrast = () => {
    setContrastActive(!contrastActive);
    if (!contrastActive) {
      document.documentElement.classList.add('dark'); // o una clase de contraste alto
      document.body.style.filter = 'contrast(1.2) brightness(0.95)';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.filter = '';
    }
  };

  const handleToggleDyslexic = () => {
    setDyslexicActive(!dyslexicActive);
    if (!dyslexicActive) {
      document.body.style.fontFamily = "'OpenDyslexic', 'Comic Sans MS', sans-serif";
    } else {
      document.body.style.fontFamily = "";
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Botón Flotante Global (Ubicado justo debajo del Asistente IA, en bottom-[16px]) */}
      <div 
        className="fixed z-[9998] group"
        style={{ bottom: '16px', right: '16px' }}
      >
        {/* Pequeño botón de cerrar (X) idéntico en tamaño y comportamiento al de cookies */}
        <button
          onClick={handleHideWidget}
          title={t.hideWidgetTooltip}
          aria-label={t.hideWidgetTooltip}
          className="absolute -top-1 -left-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 w-4 h-4 text-[9px] flex items-center justify-center border border-white/20 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[10000]"
        >
          <X className="w-2.5 h-2.5" />
        </button>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="relative flex items-center justify-center w-10 h-10 rounded-full shadow-[0_8px_24px_rgba(0,0,0,0.45)] border-2 border-amber-500/40 bg-gradient-to-br from-[#0c1220] to-[#16233b] hover:scale-105 active:scale-95 transition-all duration-300"
        >
          <span className="absolute inset-0 rounded-full border border-amber-500/20 animate-ping opacity-30"></span>
          <Scale className="w-5 h-5 text-amber-400 group-hover:rotate-12 transition-transform" />
          
          {/* Tooltip flotante al pasar el cursor */}
          <span className="absolute right-14 bg-slate-950 text-amber-400 text-[10px] font-bold px-2.5 py-1 rounded-lg border border-slate-800 opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity shadow-xl pointer-events-none">
            {t.hubTitle}
          </span>
        </button>
      </div>

      {/* Panel Desplegado de la Ventanilla Única */}
      {open && (
        <div
          className="fixed bottom-[calc(80px+env(safe-area-inset-bottom,0px))] right-4 sm:right-6 z-[9998] w-[min(410px,calc(100vw-2rem))] h-[min(580px,calc(100vh-10rem))] rounded-3xl overflow-hidden shadow-2xl border border-slate-800/80 bg-[#0b0f19] flex flex-col"
          role="dialog"
          aria-label="ADCA Compliance Hub - Ventanilla Unica de Cumplimiento"
        >
          {/* Cabecera Premium del Panel */}
          <div className="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-slate-950 to-slate-900 border-b border-slate-800/60 text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Layers className="w-4.5 h-4.5 text-amber-400" />
              </div>
              <div className="leading-tight">
                <p className="font-semibold text-xs text-white">{t.hubTitle}</p>
                <p className="text-[10px] text-emerald-400 font-mono flex items-center gap-1 font-bold">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t.status}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.closeAria}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menú de Pestañas */}
          <div className="bg-slate-950 px-2 flex border-b border-slate-900/60 overflow-x-auto text-[10px] scrollbar-none shrink-0">
            <button
              onClick={() => setActiveTab('cookies')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'cookies' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <Cookie className="w-3.5 h-3.5" /> {t.tabCookies}
            </button>
            <button
              onClick={() => setActiveTab('denuncias')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'denuncias' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <EyeOff className="w-3.5 h-3.5" /> {t.tabDenuncias}
            </button>
            <button
              onClick={() => setActiveTab('access')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'access' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <Accessibility className="w-3.5 h-3.5" /> {t.tabAccess}
            </button>
            <button
              onClick={() => setActiveTab('desist')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'desist' ? 'border-rose-500 text-rose-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <RefreshCw className="w-3.5 h-3.5" /> {t.tabDesist}
            </button>
          </div>

          {/* Cuerpo Contenido Modular */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            
            {/* PESTAÑA: COOKIES */}
            {activeTab === 'cookies' && (
              <div className="space-y-4 text-left">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl text-[11px] leading-relaxed text-slate-300">
                  {t.cookieInfo}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-900 rounded-xl text-[11px]">
                    <div>
                      <p className="font-bold text-white">{t.cookieEssential}</p>
                      <p className="text-[10px] text-slate-400">{t.cookieEssentialDesc}</p>
                    </div>
                    <span className="text-[9px] bg-slate-850 text-slate-500 font-bold px-2 py-0.5 rounded">ALWAYS ON</span>
                  </div>

                  <div className="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-900 rounded-xl text-[11px]">
                    <div>
                      <p className="font-bold text-white">{t.cookieAnalytics}</p>
                      <p className="text-[10px] text-slate-400">{t.cookieAnalyticsDesc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={cookieAnalyticsChecked} 
                        onChange={(e) => setCookieAnalyticsChecked(e.target.checked)} 
                        className="sr-only peer"
                      />
                      <div className="w-8 h-4.5 bg-slate-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-amber-500 peer-checked:after:bg-slate-950"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-2.5 bg-slate-950 border border-slate-900 rounded-xl text-[11px]">
                    <div>
                      <p className="font-bold text-white">{t.cookieMarketing}</p>
                      <p className="text-[10px] text-slate-400">{t.cookieMarketingDesc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={cookieMarketingChecked} 
                        onChange={(e) => setCookieMarketingChecked(e.target.checked)} 
                        className="sr-only peer"
                      />
                      <div className="w-8 h-4.5 bg-slate-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-400 after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-amber-500 peer-checked:after:bg-slate-950"></div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button 
                    onClick={handleSaveCookies} 
                    className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl text-[11px] transition"
                  >
                    {t.btnAcceptConfig}
                  </button>
                  <button 
                    onClick={handleAcceptAllCookies} 
                    className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-[11px] transition"
                  >
                    {t.btnAcceptAll}
                  </button>
                </div>
              </div>
            )}

            {/* PESTAÑA: CANAL DE DENUNCIAS */}
            {activeTab === 'denuncias' && (
              <form onSubmit={handleDenunciaSubmit} className="space-y-4 text-left">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl text-[11px] leading-relaxed text-slate-300">
                  <p className="text-cyan-400 font-bold mb-1"><i class="fa-solid fa-lock"></i> {t.denunciaTitle}</p>
                  {t.denunciaDesc}
                </div>

                {!denunciaSubmitted ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t.denunciaLabelType}</label>
                      <select 
                        value={denunciaType} 
                        onChange={(e) => setDenunciaType(e.target.value)} 
                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-cyan-500"
                      >
                        <option value="acoso">{t.denunciaType1}</option>
                        <option value="fraude">{t.denunciaType2}</option>
                        <option value="privacidad">{t.denunciaType3}</option>
                        <option value="otro">{t.denunciaType4}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t.denunciaLabelText}</label>
                      <textarea 
                        value={denunciaDesc} 
                        onChange={(e) => setDenunciaDesc(e.target.value)} 
                        placeholder="..." 
                        className="w-full h-20 bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-cyan-500 resize-none"
                      ></textarea>
                    </div>

                    <button 
                      type="submit" 
                      className="w-full py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-xl text-xs transition"
                    >
                      {t.btnSendDenuncia}
                    </button>
                  </div>
                ) : (
                  <div className="bg-slate-950 border border-slate-900 p-3.5 rounded-xl font-mono text-[9px] text-cyan-400 space-y-1.5 overflow-x-hidden">
                    <p>> Eliminando metadatos y firmas físicas...</p>
                    <p>> Cifrando contenido con clave RSA-4096...</p>
                    <p>> Estampando firma criptográfica TSA cualificada...</p>
                    <p className="text-emerald-400 font-bold font-sans mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" /> {t.demoConsoleSuccess}
                    </p>
                    <p className="text-white select-all break-all cursor-pointer font-bold mt-1 bg-slate-900 p-2 rounded border border-slate-800">
                      HASH_TSA: 4f8a7e3b9c2a1d0f8e7c6b5a4d3e2f10d9c8b7a6f5e4d3c2b1a09876543210ab
                    </p>
                  </div>
                )}
              </form>
            )}

            {/* PESTAÑA: ACCESIBILIDAD */}
            {activeTab === 'access' && (
              <div className="space-y-4 text-left">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl text-[11px] leading-relaxed text-slate-300">
                  <p className="text-emerald-400 font-bold mb-1">{t.accessTitle}</p>
                  {t.accessDesc}
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <button 
                    onClick={handleToggleTextBig} 
                    className={`p-3 bg-slate-950 hover:bg-slate-900 border rounded-xl text-center flex flex-col items-center gap-1.5 transition-all group ${
                      textBigActive ? 'border-emerald-500' : 'border-slate-800'
                    }`}
                  >
                    <span className="text-emerald-400 font-black text-sm group-hover:scale-105 transition-transform">A+</span>
                    <span className="text-[10px] font-bold text-white">{t.accessTextBig}</span>
                  </button>

                  <button 
                    onClick={() => {
                      setTextBigActive(false);
                      document.documentElement.style.fontSize = '';
                    }} 
                    className="p-3 bg-slate-950 hover:bg-slate-900 border border-slate-800 rounded-xl text-center flex flex-col items-center gap-1.5 transition-all group"
                  >
                    <span className="text-slate-400 font-black text-sm">A</span>
                    <span className="text-[10px] font-bold text-white">{t.accessTextNormal}</span>
                  </button>

                  <button 
                    onClick={handleToggleContrast} 
                    className={`p-3 bg-slate-950 hover:bg-slate-900 border rounded-xl text-center flex flex-col items-center gap-1.5 transition-all group ${
                      contrastActive ? 'border-emerald-500' : 'border-slate-800'
                    }`}
                  >
                    <div className="w-4 h-4 rounded-full border border-emerald-400 bg-gradient-to-r from-emerald-400 to-transparent group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] font-bold text-white">{t.accessContrast}</span>
                  </button>

                  <button 
                    onClick={handleToggleDyslexic} 
                    className={`p-3 bg-slate-950 hover:bg-slate-900 border rounded-xl text-center flex flex-col items-center gap-1.5 transition-all group ${
                      dyslexicActive ? 'border-emerald-500' : 'border-slate-800'
                    }`}
                  >
                    <Eye className="w-4.5 h-4.5 text-emerald-400 group-hover:scale-105 transition-transform" />
                    <span className="text-[10px] font-bold text-white">{t.accessDyslexia}</span>
                  </button>
                </div>
              </div>
            )}

            {/* PESTAÑA: DESISTIMIENTO */}
            {activeTab === 'desist' && (
              <form onSubmit={handleDesistSubmit} className="space-y-4 text-left">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3 rounded-xl text-[11px] leading-relaxed text-slate-300">
                  <p className="text-rose-400 font-bold mb-1">{t.desistTitle}</p>
                  {t.desistDesc}
                </div>

                {!desistSubmitted ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t.desistLabelPedido}</label>
                      <input 
                        type="text" 
                        value={desistPedido} 
                        onChange={(e) => setDesistPedido(e.target.value)} 
                        placeholder="Ej. PED-2026-90" 
                        className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-rose-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">{t.desistLabelMotivo}</label>
                      <textarea 
                        value={desistMotivo} 
                        onChange={(e) => setDesistMotivo(e.target.value)} 
                        placeholder="..." 
                        className="w-full h-16 bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-rose-500 resize-none"
                      ></textarea>
                    </div>

                    <button 
                      type="submit" 
                      className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs transition"
                    >
                      {t.btnSendDesist}
                    </button>
                  </div>
                ) : (
                  <div className="bg-slate-950 border border-slate-900 p-3.5 rounded-xl font-mono text-[9px] text-rose-400 space-y-1.5 overflow-x-hidden">
                    <p>> Comprobando plazo legal de 14 días naturales...</p>
                    <p>> Firmando resguardo digital de desistimiento...</p>
                    <p className="text-emerald-400 font-bold font-sans mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3.5 h-3.5" /> {t.demoConsoleSuccessDesist}
                    </p>
                    <p className="text-white select-all break-all cursor-pointer font-bold mt-1 bg-slate-900 p-2 rounded border border-slate-800">
                      SIGNATURE_HASH: e3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4
                    </p>
                  </div>
                )}
              </form>
            )}

          </div>

          {/* Pie de Panel */}
          <div className="p-3 bg-slate-950 border-t border-slate-900 flex justify-between items-center text-[8.5px] text-slate-500 font-mono shrink-0">
            <span>{t.footerInfo}</span>
            <span className="text-amber-400 font-bold flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-amber-400" /> ENS CONFORME
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default ComplianceWidget;
